import type { BritProtocol } from '../../../api/types';
import type { OHIFHangingProtocol } from './britToOhif';
import { britToOhifProtocol } from './britToOhif';

// ---------------------------------------------------------------------------
// Message types exchanged between the React app and the OHIF iframe
// ---------------------------------------------------------------------------

/** Messages sent FROM the React app TO the OHIF iframe */
export type BritToOhifMessage =
  | { type: 'BRIT_APPLY_PROTOCOL'; protocol: OHIFHangingProtocol }
  | { type: 'BRIT_CAPTURE_PROTOCOL'; requestId: string }
  | { type: 'BRIT_PING' };

/** Messages sent FROM the OHIF iframe TO the React app */
export type OhifToBritMessage =
  | { type: 'BRIT_OHIF_READY' }
  | { type: 'BRIT_CAPTURE_RESULT'; requestId: string; protocol: OHIFHangingProtocol | null }
  | { type: 'BRIT_PONG' };

// ---------------------------------------------------------------------------
// Send helpers
// ---------------------------------------------------------------------------

/**
 * Send a translated protocol to the OHIF iframe for immediate application.
 * Translates from Brit PACS canonical format to OHIF hanging protocol format.
 */
export function sendProtocolToOhif(
  iframe: HTMLIFrameElement,
  protocol: BritProtocol,
): void {
  const ohifProtocol = britToOhifProtocol(protocol);
  postToOhif(iframe, {
    type: 'BRIT_APPLY_PROTOCOL',
    protocol: ohifProtocol,
  });
}

/**
 * Request the current viewport state from OHIF.
 * Returns a promise that resolves with the captured OHIF protocol, or null.
 * Times out after 5 seconds.
 */
export function requestCaptureFromOhif(
  iframe: HTMLIFrameElement,
): Promise<OHIFHangingProtocol | null> {
  const requestId = `capture-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const TIMEOUT_MS = 5000;

  return new Promise<OHIFHangingProtocol | null>((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(null);
    }, TIMEOUT_MS);

    function handler(event: MessageEvent<OhifToBritMessage>) {
      if (
        event.data?.type === 'BRIT_CAPTURE_RESULT' &&
        event.data.requestId === requestId
      ) {
        clearTimeout(timer);
        window.removeEventListener('message', handler);
        resolve(event.data.protocol);
      }
    }

    window.addEventListener('message', handler);
    postToOhif(iframe, { type: 'BRIT_CAPTURE_PROTOCOL', requestId });
  });
}

/**
 * Send a ping to the OHIF iframe and wait for a pong.
 * Returns true if OHIF responds within the timeout, false otherwise.
 */
export function pingOhif(
  iframe: HTMLIFrameElement,
  timeoutMs = 2000,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(false);
    }, timeoutMs);

    function handler(event: MessageEvent<OhifToBritMessage>) {
      if (event.data?.type === 'BRIT_PONG') {
        clearTimeout(timer);
        window.removeEventListener('message', handler);
        resolve(true);
      }
    }

    window.addEventListener('message', handler);
    postToOhif(iframe, { type: 'BRIT_PING' });
  });
}

// ---------------------------------------------------------------------------
// Listener setup
// ---------------------------------------------------------------------------

/**
 * Subscribe to OHIF ready events.
 * Returns an unsubscribe function.
 */
export function onOhifReady(callback: () => void): () => void {
  function handler(event: MessageEvent<OhifToBritMessage>) {
    if (event.data?.type === 'BRIT_OHIF_READY') {
      callback();
    }
  }
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

/** Post a message to the OHIF iframe's content window */
function postToOhif(iframe: HTMLIFrameElement, message: BritToOhifMessage): void {
  try {
    iframe.contentWindow?.postMessage(message, '*');
  } catch {
    // Cross-origin or iframe not available — silently ignore
  }
}
